import React, { useMemo, useState } from 'react';
import type { CheckboxListElement } from 'models/forms/formElements/CheckboxListElement';

type Props = {
  element: CheckboxListElement;
  isEditing: boolean;
  onUpdate: (updatedElement: CheckboxListElement) => void;
  isDisabled?: boolean;
};

const parseOptions = (text: string): string[] => {
  return text
    .split(/\r?\n|,/g)
    .map((o) => o.trim())
    .filter(Boolean);
};

const CheckboxListElementComponent: React.FC<Props> = ({ element, isEditing, onUpdate, isDisabled }) => {
  const [name, setName] = useState(element.name);
  const [optionsText, setOptionsText] = useState((element.options || []).join('\n'));

  const selectedValues = useMemo(() => {
    return (element.value || '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }, [element.value]);

  const options = useMemo(() => element.options || [], [element.options]);

  const updateOptions = (text: string) => {
    setOptionsText(text);
    onUpdate({
      ...element,
      options: parseOptions(text),
    });
  };

  const toggleValue = (option: string) => {
    const next = selectedValues.includes(option)
      ? selectedValues.filter((v) => v !== option)
      : [...selectedValues, option];

    onUpdate({
      ...element,
      value: next.join(','),
    });
  };

  if (isEditing) {
    return (
      <div className="space-y-3">
        <div>
          <p className="mb-1 text-sm text-gray-700">Label</p>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              const next = e.target.value;
              setName(next);
              onUpdate({ ...element, name: next });
            }}
            className="block w-full px-3 py-2 border border-gray-300 rounded"
          />
        </div>

        <div>
          <p className="mb-1 text-sm text-gray-700">Options (comma or newline separated)</p>
          <textarea
            value={optionsText}
            onChange={(e) => updateOptions(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded min-h-[90px]"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="font-medium text-gray-700">{element.name}{element.isRequired ? ' *' : ''}</div>
      <div className="space-y-1">
        {options.map((opt) => (
          <label key={opt} className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={selectedValues.includes(opt)}
              onChange={() => toggleValue(opt)}
              disabled={isDisabled ?? false}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default CheckboxListElementComponent;
